import { useState, useCallback, useEffect } from "react";
import { AxiosError } from "axios";
// import apiClient from "../dummy_shivam/api";
import type { StyleTag } from "../dummy_shivam/index";
import { useGeneralApiCall } from "@/services/useGeneralApiCall";
import { showToast } from "@/api/notification";

interface UseTagManagementReturn {
  tags: StyleTag[];
  isLoading: boolean;
  error: string | null;
  fetchTags: () => Promise<void>;
  createTag: (name: string) => Promise<StyleTag | null>;
  updateTag: (tagId: string, name: string) => Promise<StyleTag | null>;
  deleteTag: (tagId: string) => Promise<boolean>;
  refreshTags: () => void;
}

export const useTagManagement = (): UseTagManagementReturn => {
  const [tags, setTags] = useState<StyleTag[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getApi, postApi, putApi, deleteApi } = useGeneralApiCall();

  // Fetch all tags
  const fetchTags = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await getApi<StyleTag[]>("/api/style/v1/tags/");

      const validTags = (response.data || []).filter(
        (tag): tag is StyleTag =>
          tag !== null &&
          tag !== undefined &&
          typeof tag === "object" &&
          "tag_id" in tag &&
          "name" in tag &&
          typeof tag.tag_id === "string" &&
          typeof tag.name === "string" &&
          tag.tag_id.length > 0 &&
          tag.name.length > 0
      );
      setTags(validTags);
    } catch (err) {
      const errorMessage =
        err instanceof AxiosError
          ? err.response?.data?.message || "Failed to fetch tags"
          : "An unexpected error occurred";
      setError(errorMessage);
      console.error("Error fetching tags:", err);
      setTags([]); // Set empty array on error
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Create new tag
  const createTag = useCallback(
    async (name: string): Promise<StyleTag | null> => {
      try {
        setError(null);

        const response = await postApi<StyleTag>("/api/style/v1/tags/", {
          name: name.trim(),
        });

        const newTag = response.data;
        showToast({ type: "success", message: "Tag Create is successfully" });
        if (newTag && newTag.tag_id && newTag.name) {
          setTags((prev) => [...prev, newTag]);
          return newTag;
        }

        return null;
      } catch (err: any) {
        setError(err.message);

        return null;
      }
    },
    []
  );

  // Update existing tag
  const updateTag = useCallback(
    async (tagId: string, name: string): Promise<StyleTag | null> => {
      try {
        setError(null);

        const response = await putApi<StyleTag>(`/api/style/v1/tags`, tagId, {
          name: name.trim(),
        });

        const updatedTag = response.data;
        showToast({ type: "success", message: "Tag Update is successfully" });
        if (updatedTag && updatedTag.tag_id && updatedTag.name) {
          setTags((prev) =>
            prev.map((tag) => (tag.tag_id === tagId ? updatedTag : tag))
          );
          return updatedTag;
        }

        return null;
      } catch (err) {
        const errorMessage =
          err instanceof AxiosError
            ? err.response?.data?.message || "Failed to update tag"
            : "An unexpected error occurred";
        setError(errorMessage);
        showToast({ type: "error", message: errorMessage });
        console.error("Error updating tag:", err);
        return null;
      }
    },
    []
  );

  // Delete tag
  const deleteTag = useCallback(async (tagId: string): Promise<boolean> => {
    try {
      setError(null);

      await deleteApi(`/api/style/v1/tags`, tagId);
      showToast({ type: "success", message: "Tag Delete is successfully" });
      setTags((prev) => prev.filter((tag) => tag.tag_id !== tagId));
      return true;
    } catch (err) {
      const errorMessage =
        err instanceof AxiosError
          ? err.response?.data?.message || "Failed to delete tag"
          : "An unexpected error occurred";
      setError(errorMessage);
      showToast({ type: "error", message: errorMessage });
      console.error("Error deleting tag:", err);
      return false;
    }
  }, []);

  // Refresh tags (alias for fetchTags)
  const refreshTags = useCallback(() => {
    fetchTags();
  }, [fetchTags]);

  // Initial fetch
  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  return {
    tags,
    isLoading,
    error,
    fetchTags,
    createTag,
    updateTag,
    deleteTag,
    refreshTags,
  };
};
